import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Ship } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { APP_ROUTES } from '../../../app/routePaths';
import { FOCUS_RING, useVesselAlerts, VesselAlertList } from '../../vessel-changes';
import type { EmployeeNotification } from '../api/employeeNotificationsApi';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useMyNotifications } from '../hooks/useEmployeeNotifications';
import { vesselNoticePath, withoutShownEscalations } from '../model/vesselNotices';
import { DesktopAlertSettings } from './DesktopAlertSettings';
import { VesselNoticeList } from './VesselNoticeList';

const countText = (count: number) => (count > 99 ? '99+' : String(count));

/**
 * The vessel bell (owner, 2026-10-13): vessel notices only (ETA changes, jobs to link to a voyage,
 * Westports calls omitted, PTP's or MMSW's schedule not updating, the Westports schedule due for upload, phone
 * alerts not working), apart from the normal bell. Calm by design: a
 * solid badge (red while alerts are open, amber for unread notices only), no animation. Inside, one
 * scroll area under a neutral header: "Action needed" rows pinned on top until handled, then the
 * vessel notices (unread first), and the desktop alert settings as a muted footer.
 */
export function VesselBell() {
  const auth = useAuth();
  const employeeId = Number(auth.employeeId) || 0;
  const navigate = useNavigate();
  const alerts = useVesselAlerts(employeeId > 0);
  const inbox = useMyNotifications(employeeId, 'vessel');
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead('vessel');
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const openAlerts = alerts.data ?? [];
  const unread = inbox.data?.unreadCount ?? 0;
  const teamVessels = openAlerts.filter((alert) => !alert.mine).map((alert) => alert.vesselName);
  const notices = inbox.data ? withoutShownEscalations(inbox.data.items, teamVessels) : null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (employeeId <= 0) return null;

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };
  const openNotice = (notice: EmployeeNotification) => {
    if (!notice.read) markRead.mutate(notice.id);
    go(vesselNoticePath(notice));
  };

  const alerting = openAlerts.length > 0;
  const title = alerting
    ? `${openAlerts.length} vessel alert${openAlerts.length === 1 ? ' needs' : 's need'} action`
    : unread ? `${unread} unread vessel notice${unread === 1 ? '' : 's'}` : 'Vessel alerts';
  const badge = alerting ? openAlerts.length : unread;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative rounded-lg p-1.5 transition-colors hover:bg-slate-100 ${FOCUS_RING}`}
        style={{ color: 'var(--color-text-tertiary)' }}
        title={title}
        aria-label="Vessel alerts"
        aria-expanded={open}
      >
        <Ship className="h-4 w-4" />
        {badge > 0 && (
          <span
            data-testid={alerting ? 'vessel-alert-count' : 'vessel-notice-count'}
            className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white ${alerting ? 'bg-red-500' : 'bg-amber-500'}`}
          >
            {countText(badge)}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 flex max-h-[70vh] w-[26rem] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="flex shrink-0 items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Vessel alerts</span>
            {alerting && (
              <span data-testid="vessel-alert-pill" className="rounded-full bg-slate-200 px-1.5 text-[11px] font-medium tabular-nums text-slate-700">
                {countText(openAlerts.length)}
              </span>
            )}
            <button type="button" onClick={() => go(APP_ROUTES.vesselChanges)}
              className={`ml-auto inline-flex items-center gap-0.5 rounded text-[11px] font-medium text-blue-700 hover:underline ${FOCUS_RING}`}>
              Open Vessel changes <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          <div data-testid="vessel-bell-scroll" className="min-h-0 flex-1 overflow-y-auto">
            {alerting ? (
              <VesselAlertList alerts={openAlerts} onOpen={go} />
            ) : (
              <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
                No vessel alerts – all your jobs match PTP.
              </p>
            )}
            <VesselNoticeList items={notices} unread={unread} onOpen={openNotice} onMarkAllRead={() => markAll.mutate()} />
          </div>
          <DesktopAlertSettings />
        </div>
      )}
    </div>
  );
}
